import { memo, useMemo } from 'react';

import { generateGemSpec, type GemIntensity, type GemSpec, type GemVariant } from '../../utils/gemGenerator';

type GemSvgViewProps = {
  seed: string;
  variant: GemVariant;
  size?: number;
  intensity?: GemIntensity;
};

function GemSvgInner({ spec }: { spec: GemSpec }) {
  const clipId = `gem-clip-${spec.seed}-${spec.variant}`;

  return (
    <svg width={spec.size} height={spec.size} viewBox={`0 0 ${spec.size} ${spec.size}`} aria-hidden="true">
      <defs>
        <linearGradient id={`${clipId}-glow`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={spec.palette.light} stopOpacity="0.9" />
          <stop offset="1" stopColor={spec.palette.dark} stopOpacity="0.95" />
        </linearGradient>
      </defs>

      {spec.variant === 'complete' ? (
        <circle
          cx={spec.size * 0.5}
          cy={spec.size * 0.56}
          r={spec.size * 0.42}
          fill={spec.palette.glow}
        />
      ) : null}

      <g>
        {spec.facets.map((facet, index) => (
          <path
            key={`${spec.seed}-facet-${index}`}
            d={facet.d}
            fill={facet.fill}
            opacity={facet.opacity ?? 1}
          />
        ))}
      </g>

      <path
        d={spec.outlineD}
        fill="none"
        stroke={spec.variant === 'complete' ? spec.palette.light : spec.palette.dark}
        strokeOpacity={spec.variant === 'complete' ? 0.55 : 0.35}
        strokeWidth={0.6}
      />

      {spec.sparkles.map((sparkle, index) => (
        <circle
          key={`${spec.seed}-sparkle-${index}`}
          cx={sparkle.cx}
          cy={sparkle.cy}
          r={sparkle.size}
          fill="#FFFFFF"
          opacity={sparkle.opacity}
        />
      ))}
    </svg>
  );
}

export const GemSvgView = memo(function GemSvgView({
  seed,
  variant,
  size = 22,
  intensity = 'normal',
}: GemSvgViewProps) {
  const spec = useMemo(() => generateGemSpec(seed, variant, size, intensity), [intensity, seed, size, variant]);
  return <GemSvgInner spec={spec} />;
});
